import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getHistory } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { FileText, Calendar, TrendingUp, Truck, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const statusLabels = {
  draft: 'Borrador',
  sent: 'Enviada',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
  expired: 'Expirado',
  delivered: 'Entregado'
};

const StatusBadge = ({ status }) => {
  return (
    <span className={`status-badge status-${status}`}>
      {statusLabels[status] || status}
    </span>
  );
};

export const HistoryPage = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    loadHistory(parseInt(selectedYear));
  }, [selectedYear]);

  const loadHistory = async (year) => {
    setLoading(true);
    try {
      const { data } = await getHistory(year);
      setHistory(data);
      setAvailableYears(data.available_years || [year]);
    } catch (e) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  const quarterNames = {
    Q1: 'Enero - Marzo',
    Q2: 'Abril - Junio',
    Q3: 'Julio - Septiembre',
    Q4: 'Octubre - Diciembre'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Historial</h1>
            <p className="text-stone-500 text-sm mt-1">Documentos por trimestre</p>
          </div>
          <PageHelpButton moduleKey="history" variant="icon" />
        </div>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-32 rounded-xl" data-testid="year-select">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((year) => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Quarters */}
      <Accordion type="multiple" className="space-y-4" defaultValue={["Q1", "Q2", "Q3", "Q4"]}>
        {history?.quarters && Object.entries(history.quarters).map(([quarter, data]) => (
          <AccordionItem 
            key={quarter} 
            value={quarter}
            className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden"
          >
            <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline hover:bg-orange-50" data-testid={`quarter-trigger-${quarter}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full pr-4 gap-2 sm:gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-[#FF6B4A] text-sm sm:text-base">{quarter}</span>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-sm sm:text-base">{quarterNames[quarter]}</p>
                    <p className="text-xs sm:text-sm text-stone-500">
                      {data.invoice_count} fact. · {data.quote_count} pres. · {data.delivery_count} alb.
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right ml-13 sm:ml-0">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Facturado</p>
                  <p className="text-base sm:text-lg font-mono font-semibold text-[#FF6B4A]">
                    {formatCurrency(data.total_invoiced)}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 pb-6">
              <div className="space-y-6">
                {/* Invoices */}
                {data.invoices.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-stone-500" />
                      <h3 className="text-sm font-medium text-stone-700">Facturas</h3>
                    </div>
                    <div className="space-y-2">
                      {data.invoices.map((invoice) => (
                        <div 
                          key={invoice.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between py-2 px-3 bg-stone-50 rounded-xl gap-1 sm:gap-4"
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                            <Link 
                              to={`/invoices/${invoice.id}`}
                              className="font-mono text-sm text-[#FF6B4A] hover:underline"
                            >
                              {invoice.invoice_number}
                            </Link>
                            <span className="text-sm text-stone-600">{invoice.client_name}</span>
                            <span className="text-xs text-stone-400 hidden sm:inline">{invoice.issue_date}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4">
                            <StatusBadge status={invoice.status} />
                            <span className="font-mono text-sm font-medium">
                              {formatCurrency(invoice.total)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quotes */}
                {data.quotes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <ClipboardList className="w-4 h-4 text-stone-500" />
                      <h3 className="text-sm font-medium text-stone-700">Presupuestos</h3>
                    </div>
                    <div className="space-y-2">
                      {data.quotes.map((quote) => (
                        <div 
                          key={quote.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between py-2 px-3 bg-stone-50 rounded-xl gap-1 sm:gap-4"
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                            <span className="font-mono text-sm text-[#FF6B4A]">{quote.quote_number}</span>
                            <span className="text-sm text-stone-600">{quote.client_name}</span>
                            <span className="text-xs text-stone-400 hidden sm:inline">{quote.issue_date}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4">
                            <StatusBadge status={quote.status} />
                            <span className="font-mono text-sm font-medium">
                              {formatCurrency(quote.total)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delivery Notes */}
                {data.delivery_notes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Truck className="w-4 h-4 text-stone-500" />
                      <h3 className="text-sm font-medium text-stone-700">Albaranes</h3>
                    </div>
                    <div className="space-y-2">
                      {data.delivery_notes.map((note) => (
                        <div 
                          key={note.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between py-2 px-3 bg-stone-50 rounded-xl gap-1 sm:gap-4"
                        >
                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                            <span className="font-mono text-sm text-[#FF6B4A]">{note.delivery_number}</span>
                            <span className="text-sm text-stone-600">{note.client_name}</span>
                            <span className="text-xs text-stone-400 hidden sm:inline">{note.issue_date}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4">
                            <StatusBadge status={note.status} />
                            <span className="font-mono text-sm font-medium">
                              {formatCurrency(note.total)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.invoices.length === 0 && data.quotes.length === 0 && data.delivery_notes.length === 0 && (
                  <p className="text-center text-stone-500 py-4">No hay documentos en este trimestre</p>
                )}

                {/* Quarter Summary */}
                <div className="flex justify-end pt-4 border-t border-stone-100">
                  <div className="space-y-1 text-right">
                    <div className="flex items-center gap-6">
                      <span className="text-sm text-stone-500">Total facturado:</span>
                      <span className="font-mono font-medium">{formatCurrency(data.total_invoiced)}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-sm text-stone-500">Total cobrado:</span>
                      <span className="font-mono font-medium text-green-600">{formatCurrency(data.total_paid)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default HistoryPage;
